import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  InputAdornment,
  IconButton,
  Chip,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  QrCode as QrCodeIcon,
  Close as CloseIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import * as productService from '../../services/productService';
import { getProductWithWarehouseInventory } from '../../services/productService';
import { Product, ProductVariant } from '../../types/product';
import { ProductAttribute } from '../../types/product';
import { Warehouse } from '../../types/warehouse';

interface ProductAttributeSearchProps {
  onSelectVariant: (product: Product, variant: ProductVariant) => void;
  onSelectVariants?: (items: { product: Product; variant: ProductVariant }[]) => void;
  open: boolean;
  onClose: () => void;
  selectedCategories?: string[]; // التصنيفات المختارة من إعدادات الصفحة
  productSelectionMode?: 'all' | 'category' | 'specific'; // نمط اختيار المنتجات
  multiSelect?: boolean; // وضع الاختيار المتعدد
}

const ProductAttributeSearch: React.FC<ProductAttributeSearchProps> = ({
  onSelectVariant,
  onSelectVariants,
  open,
  onClose,
  selectedCategories = [],
  productSelectionMode = 'all',
  multiSelect = false
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [attributes, setAttributes] = useState<ProductAttribute[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [barcode, setBarcode] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string[]>>({});
  const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false);
  const [selectedVariantForInventory, setSelectedVariantForInventory] = useState<{product: Product, variant: ProductVariant} | null>(null);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [selectedItems, setSelectedItems] = useState<{ product: Product; variant: ProductVariant }[]>([]);
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  // Focus barcode input when dialog opens
  useEffect(() => {
    if (open && barcodeInputRef.current) {
      setTimeout(() => {
        barcodeInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Fetch initial data (attributes and warehouses only)
  useEffect(() => {
    if (open) {
      fetchInitialData();
    }
  }, [open]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      // Fetch only attributes and warehouses initially - no products
      const { apiWithContext } = await import('../../services/api');
      
      const [attributesData, warehousesData] = await Promise.all([
        apiWithContext.get('/product-attributes', 'invoice').then(res => res.data),
        apiWithContext.get('/warehouses?status=active', 'invoice').then(res => res.data)
      ]);
      
      console.log('Attributes data:', attributesData);
      
      // Start with empty products list - will be populated by search
      setProducts([]);
      setFilteredProducts([]);
      setAttributes(attributesData);
      setWarehouses(warehousesData.warehouses || warehousesData || []);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Dynamic product search function
  const searchProducts = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setProducts([]);
      setFilteredProducts([]);
      return;
    }

    setIsLoadingProducts(true);
    try {
      const { apiWithContext } = await import('../../services/api');
      const response = await apiWithContext.get(
        `/products?search=${encodeURIComponent(searchTerm)}&limit=50`,
        'invoice'
      );
      const searchResults = response.data.products || [];
      
      console.log(`🔍 ProductAttributeSearch - نتائج البحث: ${searchResults.length} منتج`);
      
      setProducts(searchResults);
      setFilteredProducts(searchResults);
    } catch (error) {
      console.error('خطأ في البحث عن المنتجات:', error);
      setProducts([]);
      setFilteredProducts([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // Handle search with delay
  const handleProductSearch = (searchTerm: string) => {
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // إذا كان البحث فارغاً، امسح النتائج فوراً بدون تأخير
    if (!searchTerm.trim()) {
      setProducts([]);
      setFilteredProducts([]);
      setIsLoadingProducts(false);
      return;
    }
    
    // إذا كان البحث أقل من 3 أحرف، لا تبحث بعد (تجنب النتائج الكثيرة)
    if (searchTerm.trim().length < 3) {
      setProducts([]);
      setFilteredProducts([]);
      setIsLoadingProducts(false);
      return;
    }
    
    // Set new timeout for delayed search - زيادة الوقت إلى 500ms لتقليل عدد الطلبات
    const timeout = setTimeout(() => {
      searchProducts(searchTerm);
    }, 500);
    
    setSearchTimeout(timeout);
  };

  // Handle barcode search - search directly in database
  const handleBarcodeSearch = async () => {
    if (!barcode.trim()) return;

    setIsLoadingProducts(true);
    try {
      const { apiWithContext } = await import('../../services/api');
      const response = await apiWithContext.get(
        `/products/barcode/${encodeURIComponent(barcode.trim())}`,
        'invoice'
      );
      
      if (response.data.success && response.data.product) {
        const product = response.data.product;
        const variant = response.data.variant;
        
        console.log(`✅ تم العثور على المنتج بالباركود: ${product.name}`);
        if (multiSelect || onSelectVariants) {
          setSelectedItems(prev => {
            const exists = prev.some(item => item.variant._id === variant._id);
            if (exists) {
              return prev.filter(item => item.variant._id !== variant._id);
            }
            return [...prev, { product, variant }];
          });
        } else if (onSelectVariant) {
          onSelectVariant(product, variant);
          onClose();
        }
      } else {
        alert('لم يتم العثور على منتج بهذا الباركود');
      }
    } catch (error) {
      console.error('خطأ في البحث بالباركود:', error);
      alert('لم يتم العثور على منتج بهذا الباركود');
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // Handle pressing Enter in barcode field
  const handleBarcodeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBarcodeSearch();
    }
  };

  // Handle attribute selection
  const handleAttributeChange = (attributeName: string, values: string[]) => {
    setSelectedAttributes(prev => {
      const updated = { ...prev };
      if (values.length === 0) {
        delete updated[attributeName];
      } else {
        updated[attributeName] = values;
      }
      return updated;
    });
  };

  // Handle search term changes with dynamic search
  useEffect(() => {
    handleProductSearch(searchTerm);
  }, [searchTerm]);

  // Filter products based on selected attributes and page category settings
  useEffect(() => {
    // إذا لم يكن هناك منتجات، لا تقم بأي عمليات فلترة
    if (products.length === 0) {
      setFilteredProducts([]);
      return;
    }
    
    let results = products;
    
    // أولاً: تطبيق فلترة التصنيفات من إعدادات الصفحة
    if (productSelectionMode === 'category' && selectedCategories.length > 0) {
      console.log('🔍 ProductAttributeSearch - تطبيق فلترة التصنيفات:', selectedCategories);
      
      results = results.filter(product => {
        // التحقق من أن المنتج يحتوي على متغير واحد على الأقل ينتمي لأحد التصنيفات المختارة
        const hasMatchingVariant = product.variants.some(variant => {
          // التحقق من تصنيفات المتغير
          if (variant.categories && variant.categories.length > 0) {
            const variantMatch = variant.categories.some(categoryId => selectedCategories.includes(categoryId));
            if (variantMatch) {
              return true;
            }
          }
          
          // التحقق من تصنيفات الباركودات في المتغير
          const barcodeMatch = variant.barcodes.some(barcode => {
            return barcode.categories && barcode.categories.some(categoryId =>
              selectedCategories.includes(categoryId)
            );
          });
          
          return barcodeMatch;
        });
        
        return hasMatchingVariant;
      });
    }
    
    // ثانياً: تطبيق فلترة الخصائص المختارة
    if (Object.keys(selectedAttributes).length > 0) {
      results = results.filter(product => {
        // Keep products that have at least one variant matching ALL selected attribute criteria
        return product.variants.some(variant => {
          // Check if this variant matches all selected attribute criteria
          return Object.entries(selectedAttributes).every(([attrName, selectedValues]) => {
            const variantValue = variant.attributes[attrName];
            return selectedValues.includes(variantValue);
          });
        });
      });
    }
    
    setFilteredProducts(results);
  }, [selectedAttributes, products, selectedCategories, productSelectionMode]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Collect all possible values for each attribute
  const getAttributeValues = (attributeName: string): string[] => {
    const values = new Set<string>();
    
    products.forEach(product => {
      product.variants.forEach(variant => {
        if (variant.attributes[attributeName]) {
          values.add(variant.attributes[attributeName]);
        }
      });
    });
    
    return Array.from(values);
  };

  // Handle showing inventory for a variant
  const handleShowInventory = async (product: Product, variant: ProductVariant) => {
    console.log('🚀 فتح نافذة المخزون للمنتج:', product.name);
    console.log('🔍 بيانات المتغير:', variant);
    
    // التأكد من وجود معرف المنتج
    if (!product._id) {
      console.error('❌ معرف المنتج غير موجود');
      return;
    }
    
    try {
      // استخدام نفس الطريقة المستخدمة في صفحة المنتجات مع إرسال سياق الصلاحيات
      const productWithInventory = await getProductWithWarehouseInventory(product._id, 'invoice');
      console.log('📦 المنتج مع بيانات المخزون:', productWithInventory);
      
      if (productWithInventory.variants) {
        const updatedVariant = productWithInventory.variants.find(v => v._id === variant._id);
        if (updatedVariant && updatedVariant.warehouseInventory) {
          console.log('✅ تم العثور على بيانات المخزون:', updatedVariant.warehouseInventory);
          setSelectedVariantForInventory({ product, variant: updatedVariant });
        } else {
          console.log('⚠️ لم يتم العثور على بيانات المخزون للمتغير');
          setSelectedVariantForInventory({ product, variant });
        }
      } else {
        console.log('⚠️ لم يتم العثور على متغيرات في المنتج');
        setSelectedVariantForInventory({ product, variant });
      }
    } catch (error) {
      console.error('❌ خطأ في جلب بيانات المخزون:', error);
      // في حالة الخطأ، استخدم البيانات الموجودة
      setSelectedVariantForInventory({ product, variant });
    }
    
    setInventoryDialogOpen(true);
  };

  // Close inventory dialog
  const handleCloseInventoryDialog = () => {
    setInventoryDialogOpen(false);
    setSelectedVariantForInventory(null);
  };

  // Reset form when dialog closes
  const handleDialogClose = () => {
    // Clear all search inputs and results
    setBarcode('');
    setSearchTerm('');
    setSelectedAttributes({});
    setProducts([]);
    setFilteredProducts([]);
    setSelectedItems([]);
    
    // Clear any pending search timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      setSearchTimeout(null);
    }
    
    // Call the original onClose
    onClose();
  };

  const handleToggleSelection = (product: Product, variant: ProductVariant) => {
    setSelectedItems(prev => {
      const exists = prev.some(item => item.variant._id === variant._id);
      if (exists) {
        return prev.filter(item => item.variant._id !== variant._id);
      }
      return [...prev, { product, variant }];
    });
  };

  const isItemSelected = (variantId: string) => {
    return selectedItems.some(item => item.variant._id === variantId);
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ style: { minHeight: '70vh' } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">البحث عن متغيرات المنتج</Typography>
          <IconButton onClick={handleDialogClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          {/* Barcode Scanner */}
          <TextField
            fullWidth
            label="قراءة الباركود"
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            onKeyDown={handleBarcodeKeyDown}
            margin="normal"
            variant="outlined"
            inputRef={barcodeInputRef}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleBarcodeSearch} edge="end">
                    <QrCodeIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          {/* General Search */}
          <TextField
            fullWidth
            label="بحث عن منتج (ابدأ بالكتابة)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            margin="normal"
            variant="outlined"
            placeholder="ابدأ بكتابة اسم المنتج للبحث..."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {isLoadingProducts ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ mr: 1 }}>
                        جاري البحث...
                      </Typography>
                    </Box>
                  ) : (
                    <SearchIcon />
                  )}
                </InputAdornment>
              ),
            }}
          />
          
          {/* Attribute Filters */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              تصفية حسب خصائص المنتج: ({attributes.length} خاصية)
            </Typography>
            {attributes.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                لا توجد خصائص منتج متاحة
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {attributes.map(attribute => {
                // استخدام القيم الفعلية من المنتجات الموجودة في نتائج البحث
                // بدلاً من القيم المعرّفة في الخاصية
                const actualValues = getAttributeValues(attribute.name);
                const displayOptions = actualValues.length > 0 ? actualValues : (attribute.values || []);
                
                return (
                  <Autocomplete
                    key={attribute._id}
                    multiple
                    options={displayOptions}
                    filterSelectedOptions
                    value={selectedAttributes[attribute.name] || []}
                    onChange={(_, newValue) => handleAttributeChange(attribute.name, newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={attribute.name}
                        size="small"
                        sx={{ minWidth: 200 }}
                        helperText={actualValues.length > 0 ? `${actualValues.length} قيمة متاحة` : ''}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          size="small"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                  />
                );
              })}
            </Box>
          </Box>
          
          {/* Selected Items Summary */}
          {selectedItems.length > 0 && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, border: '1px solid', borderColor: 'primary.main' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.primary', fontWeight: 500 }}>
                العناصر المختارة ({selectedItems.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedItems.map((item, index) => (
                  <Chip
                    key={`${item.product._id}-${item.variant._id}-${index}`}
                    label={`${item.product.name} - ${item.variant.barcodes[0]?.code || ''}`}
                    size="small"
                    onDelete={() => handleToggleSelection(item.product, item.variant)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
          
          {/* Products Table */}
          <Typography variant="subtitle1" gutterBottom>
            {searchTerm.trim() ? (
              `النتائج (${filteredProducts.reduce((count, product) => {
                return count + product.variants.filter(variant => {
                  // تطبيق نفس منطق الفلترة المستخدم في عرض المتغيرات
                  // أولاً: فلترة حسب التصنيفات المختارة من إعدادات الصفحة
                  if (productSelectionMode === 'category' && selectedCategories.length > 0) {
                    let variantAllowed = false;
                    
                    if (variant.categories && variant.categories.length > 0) {
                      variantAllowed = variant.categories.some(categoryId => selectedCategories.includes(categoryId));
                    }
                    
                    if (!variantAllowed) {
                      variantAllowed = variant.barcodes.some(barcode => {
                        return barcode.categories && barcode.categories.some(categoryId =>
                          selectedCategories.includes(categoryId)
                        );
                      });
                    }
                    
                    if (!variantAllowed) return false;
                  }
                  
                  // ثانياً: فلترة حسب الخصائص المختارة
                  if (Object.keys(selectedAttributes).length === 0) return true;
                  
                  return Object.entries(selectedAttributes).every(([attrName, selectedValues]) => {
                    const variantValue = variant.attributes[attrName];
                    return selectedValues.includes(variantValue);
                  });
                }).length;
              }, 0)} متغير)`
            ) : (
              'ابدأ بالبحث لعرض المنتجات'
            )}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>اسم المنتج</TableCell>
                  <TableCell>متغيرات المنتج</TableCell>
                  <TableCell>الباركود</TableCell>
                  <TableCell>السعر</TableCell>
                  <TableCell>إجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts.map(product => (
                  // Render each variant for the product
                  product.variants.filter(variant => {
                    // أولاً: فلترة حسب التصنيفات المختارة من إعدادات الصفحة
                    if (productSelectionMode === 'category' && selectedCategories.length > 0) {
                      // التحقق من تصنيفات المتغير
                      let variantAllowed = false;
                      
                      if (variant.categories && variant.categories.length > 0) {
                        variantAllowed = variant.categories.some(categoryId => selectedCategories.includes(categoryId));
                      }
                      
                      // إذا لم يكن المتغير مسموحاً، تحقق من تصنيفات الباركودات
                      if (!variantAllowed) {
                        variantAllowed = variant.barcodes.some(barcode => {
                          return barcode.categories && barcode.categories.some(categoryId =>
                            selectedCategories.includes(categoryId)
                          );
                        });
                      }
                      
                      if (!variantAllowed) {
                        console.log(`❌ رفض المتغير من المنتج ${product.name} - لا ينتمي للتصنيفات المختارة`);
                        return false;
                      }
                    }
                    
                    // ثانياً: فلترة حسب الخصائص المختارة
                    if (Object.keys(selectedAttributes).length === 0) return true;
                    
                    return Object.entries(selectedAttributes).every(([attrName, selectedValues]) => {
                      const variantValue = variant.attributes[attrName];
                      return selectedValues.includes(variantValue);
                    });
                  }).map(variant => (
                    <TableRow key={`${product._id}-${variant._id}`}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              cursor: 'pointer',
                              color: 'primary.main',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => handleShowInventory(product, variant)}
                          >
                            {product.name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleShowInventory(product, variant)}
                            sx={{ color: 'primary.main' }}
                          >
                            <InventoryIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {Object.entries(variant.attributes).map(([key, value], idx) => (
                          <Chip 
                            key={idx}
                            label={`${key}: ${value}`} 
                            size="small" 
                            sx={{ m: 0.5 }}
                          />
                        ))}
                      </TableCell>
                      <TableCell>
                        {variant.barcodes.map((barcode, idx) => (
                          <Typography key={idx} variant="body2">
                            {barcode.code}
                            {barcode.isDefault && ' (افتراضي)'}
                          </Typography>
                        ))}
                      </TableCell>
                      <TableCell>
                        {variant.barcodes.length > 0 && 
                          `${variant.barcodes[0].retailPrice} ريال`}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant={isItemSelected(variant._id || '') ? 'outlined' : 'contained'}
                          color={isItemSelected(variant._id || '') ? 'success' : 'primary'}
                          onClick={() => {
                            if (multiSelect || onSelectVariants) {
                              handleToggleSelection(product, variant);
                            } else if (onSelectVariant) {
                              onSelectVariant(product, variant);
                              onClose();
                            }
                          }}
                        >
                          {isItemSelected(variant._id || '') ? '✓ تم الاختيار' : (multiSelect || onSelectVariants ? 'إضافة' : 'اختيار')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ))}
                {!searchTerm.trim() && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Box sx={{ py: 4 }}>
                        <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary">
                          ابدأ بكتابة اسم المنتج في حقل البحث
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          أو استخدم قارئ الباركود للبحث السريع
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {searchTerm.trim() && filteredProducts.length === 0 && !isLoadingProducts && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Box sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          لا توجد نتائج مطابقة للبحث "{searchTerm}"
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          جرب كلمات بحث أخرى أو استخدم الباركود
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {isLoadingProducts && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Box sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          جاري البحث عن المنتجات...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleDialogClose} color="inherit">
          إلغاء
        </Button>
        {(multiSelect || onSelectVariants) && filteredProducts.length > 0 && (
          <Button
            onClick={() => {
              // تحديد جميع المنتجات المعروضة
              const allVisibleItems: { product: Product; variant: ProductVariant }[] = [];
              filteredProducts.forEach(product => {
                product.variants.forEach(variant => {
                  // تطبيق فلترة الخصائص المختارة فقط
                  if (Object.keys(selectedAttributes).length > 0) {
                    const matchesAllAttributes = Object.entries(selectedAttributes).every(([attrName, selectedValues]) => {
                      const variantValue = variant.attributes[attrName];
                      return selectedValues.includes(variantValue);
                    });
                    if (!matchesAllAttributes) return;
                  }
                  allVisibleItems.push({ product, variant });
                });
              });
              setSelectedItems(allVisibleItems);
            }}
            variant="outlined"
            color="secondary"
            sx={{ mr: 1 }}
          >
            تحديد الكل ({filteredProducts.reduce((count, product) => {
              return count + product.variants.filter(variant => {
                if (Object.keys(selectedAttributes).length > 0) {
                  return Object.entries(selectedAttributes).every(([attrName, selectedValues]) => {
                    const variantValue = variant.attributes[attrName];
                    return selectedValues.includes(variantValue);
                  });
                }
                return true;
              }).length;
            }, 0)})
          </Button>
        )}
        {(multiSelect || onSelectVariants) && selectedItems.length > 0 && (
          <Button
            onClick={() => {
              if (onSelectVariants) {
                onSelectVariants(selectedItems);
              }
              handleDialogClose();
            }}
            variant="contained"
            color="primary"
          >
            إضافة المحدد ({selectedItems.length})
          </Button>
        )}
      </DialogActions>
    </Dialog>

    {/* Inventory Dialog */}
    <Dialog
      open={inventoryDialogOpen}
      onClose={handleCloseInventoryDialog}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            كميات المخزون - {selectedVariantForInventory?.product.name}
          </Typography>
          <IconButton onClick={handleCloseInventoryDialog} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {selectedVariantForInventory && (() => {
          // طباعة تشخيصية للمتغير المحدد
          console.log('🔍 تشخيص المتغير المحدد:', {
            productName: selectedVariantForInventory.product.name,
            variantId: selectedVariantForInventory.variant._id,
            warehouseInventory: selectedVariantForInventory.variant.warehouseInventory,
            warehouseInventoryLength: selectedVariantForInventory.variant.warehouseInventory?.length || 0,
            allVariantKeys: Object.keys(selectedVariantForInventory.variant)
          });
          
          return (
          <Box>
            {/* Variant Details */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                تفاصيل المتغير:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {Object.entries(selectedVariantForInventory.variant.attributes).map(([key, value], idx) => (
                  <Chip
                    key={idx}
                    label={`${key}: ${value}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>

            {/* Inventory Table */}
            <Typography variant="subtitle1" gutterBottom>
              الكميات في المستودعات:
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>المستودع</TableCell>
                    <TableCell align="right">الكمية الحالية</TableCell>
                    <TableCell align="right">الكمية المحجوزة</TableCell>
                    <TableCell align="right">إجمالي الكمية</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {warehouses.map((warehouse) => {
                    // البحث عن بيانات المخزون لهذا المستودع
                    const inventory = selectedVariantForInventory.variant.warehouseInventory?.find(
                      inv => inv.warehouseId === warehouse._id
                    );
                    
                    // استخدام الأسماء الصحيحة للحقول - جرب جميع الاحتمالات
                    const currentQuantity = inventory?.availableQuantity || inventory?.quantity || (inventory as any)?.currentQuantity || 0;
                    const reservedQuantity = inventory?.reservedQuantity || 0;
                    const totalQuantity = currentQuantity + reservedQuantity;
                    
                    // طباعة تشخيصية مفصلة لمعرفة البيانات الفعلية
                    console.log(`🔍 تشخيص المستودع ${warehouse.name}:`, {
                      warehouseId: warehouse._id,
                      hasInventory: !!inventory,
                      inventory: inventory,
                      availableQuantity: inventory?.availableQuantity,
                      quantity: inventory?.quantity,
                      reservedQuantity: inventory?.reservedQuantity,
                      calculatedCurrentQuantity: currentQuantity,
                      allKeys: inventory ? Object.keys(inventory) : 'لا يوجد مخزون'
                    });
                    
                    return (
                      <TableRow key={warehouse._id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {warehouse.name}
                          </Typography>
                          {warehouse.location && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {warehouse.location}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color={currentQuantity > 0 ? 'success.main' : 'text.secondary'}
                            fontWeight="bold"
                          >
                            {currentQuantity}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="warning.main">
                            {reservedQuantity}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color={totalQuantity > 0 ? 'primary.main' : 'text.secondary'}
                          >
                            {totalQuantity}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {warehouses.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary">
                          لا توجد مستودعات متاحة
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Barcodes */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                الباركودات:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedVariantForInventory.variant.barcodes.map((barcode, idx) => (
                  <Chip
                    key={idx}
                    label={`${barcode.code} - ${barcode.retailPrice} ريال${barcode.isDefault ? ' (افتراضي)' : ''}`}
                    size="small"
                    color={barcode.isDefault ? 'primary' : 'default'}
                  />
                ))}
              </Box>
            </Box>
          </Box>
          );
        })()}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleCloseInventoryDialog} color="inherit">
          إغلاق
        </Button>
        {selectedVariantForInventory && (
          <Button
            onClick={() => {
              if (multiSelect || onSelectVariants) {
                handleToggleSelection(selectedVariantForInventory.product, selectedVariantForInventory.variant);
                handleCloseInventoryDialog();
              } else if (onSelectVariant) {
                onSelectVariant(selectedVariantForInventory.product, selectedVariantForInventory.variant);
                handleCloseInventoryDialog();
                onClose();
              }
            }}
            variant="contained"
            color="primary"
          >
            {multiSelect || onSelectVariants ? 'إضافة للقائمة' : 'اختيار هذا المتغير'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
    </>
  );
};

export default ProductAttributeSearch;
